import { RenameDocumentDialog } from '@/components/rename-document-dialog'
import { useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, EllipsisVerticalIcon } from 'lucide-react'
import {
	Badge,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import {
	useCreateDocumentMutation,
	useDeleteDocumentMutation,
	useListDocumentTemplatesQuery,
	useListPropertyDocumentsQuery,
	useUpdateDocumentStatusMutation,
} from '@/api'
import { SelectTemplateDialog } from '@/components/select-template-dialog'
import { formatPropertyAddress } from '@/lib/property-address'
import { CreateDocumentWizard } from '@/components/create-document-wizard'
import { toWizardProperty } from '@/components/create-document-wizard/from-property'
import type { CreateDocumentPayload } from '@/components/create-document-wizard/types'
import type { Deal, DocumentStatus, DocumentTemplate, DocumentType, Property } from '@/types'
import {
	DEFAULT_LOCALE,
	DOCUMENT_STATUS_KEYS, DOCUMENT_STATUS_VARIANTS,
	DOCUMENT_TYPE_KEYS,
} from '@/types'

type SortField = 'title' | 'createdAt'
type SortDir   = 'asc' | 'desc'

const ALL_STATUSES: DocumentStatus[] = ['DRAFT', 'DATA_FILLED', 'SIGNED_BY_ME', 'SENT', 'SIGNED_BY_TENANT', 'SIGNED_BY_BOTH']
const ALL_TYPES:    DocumentType[]   = ['LEASE_CONTRACT', 'ACT_TRANSFER', 'ACT_RETURN', 'APPENDIX', 'REGISTRATION_APPLICATION', 'OTHER']

interface PropertyDocumentsSectionProps {
	property: Property
	createOpen?: boolean
	onCreateOpenChange?: (open: boolean) => void
}

function formatCreatedAt(iso: string): string {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export function PropertyDocumentsSection({ property, createOpen, onCreateOpenChange }: PropertyDocumentsSectionProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const currentHref = useRouterState({ select: (s) => s.location.href })

	const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null)
	const [internalSelectOpen, setInternalSelectOpen] = useState(false)
	const selectOpen    = createOpen         ?? internalSelectOpen
	const setSelectOpen = onCreateOpenChange ?? setInternalSelectOpen
	const [chosenTemplate, setChosenTemplate] = useState<DocumentTemplate | null>(null)
	const [fillOpen, setFillOpen] = useState(false)
	const [typeFilter,   setTypeFilter]   = useState<DocumentType | 'all'>('all')
	const [statusFilter, setStatusFilter] = useState<DocumentStatus | 'all'>('all')
	const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({ field: 'createdAt', dir: 'desc' })

	const { data: docsData, isLoading } = useListPropertyDocumentsQuery({
		entityId: property.id,
		type:     typeFilter   === 'all' ? undefined : typeFilter,
		status:   statusFilter === 'all' ? undefined : statusFilter,
	})
	const { data: tplData } = useListDocumentTemplatesQuery({ page: 1, limit: 50 })
	const [createDocument]       = useCreateDocumentMutation()
	const [deleteDocument]       = useDeleteDocumentMutation()
	const [updateDocumentStatus] = useUpdateDocumentStatusMutation()

	const docs      = docsData?.documents ?? []
	const templates = tplData?.templates  ?? []

	// Stub deal so CreateDocumentWizard can pre-fill subject step from property data.
	const wizardDeal: Deal = {
		id:              property.id,
		propertyId:      property.id,
		propertyTitle:   formatPropertyAddress(property.city, property.address),
		propertyAddress: formatPropertyAddress(property.city, property.address),
		stage:           'NEW',
		status:          'ACTIVE',
		createdAt:       new Date(0).toISOString(),
		updatedAt:       new Date(0).toISOString(),
	}

	function handleTemplateConfirmed(tpl: DocumentTemplate) {
		setChosenTemplate(tpl)
		setSelectOpen(false)
		setFillOpen(true)
	}

	async function handleDocumentSaved(payload: {
		template: DocumentTemplate
		title:    string
		data:     CreateDocumentPayload
	}) {
		const doc = await createDocument({
			templateId:     payload.template.id,
			type:           (payload.data.documentType ?? payload.template.type) as DocumentType,
			title:          payload.title,
			entityType:     'PROPERTY',
			entityId:       property.id,
			filledDataJson: JSON.stringify(payload.data),
		}).unwrap()
		if (doc?.id) {
			await updateDocumentStatus({ id: doc.id, body: { status: 'DATA_FILLED' } }).unwrap()
		}
	}

	function toggleSort(field: SortField) {
		setSort((prev) => prev.field === field
			? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
			: { field, dir: 'asc' },
		)
	}

	const sorted = [...docs].sort((a, b) => {
		const dir = sort.dir === 'asc' ? 1 : -1
		if (sort.field === 'title') return a.title.localeCompare(b.title) * dir
		return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir
	})

	if (isLoading) {
		return <Spinner size="sm" />
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as DocumentType | 'all')}>
					<SelectTrigger className="h-8 w-auto gap-1.5 px-3 py-2 text-sm text-(--color-muted-foreground)">
						<SelectValue placeholder={t('deals.documents.filters.type')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('deals.documents.filters.type')}</SelectItem>
						{ALL_TYPES.map((tp) => (
							<SelectItem key={tp} value={tp}>{t(DOCUMENT_TYPE_KEYS[tp])}</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as DocumentStatus | 'all')}>
					<SelectTrigger className="h-8 w-auto gap-1.5 px-3 py-2 text-sm text-(--color-muted-foreground)">
						<SelectValue placeholder={t('deals.documents.filters.status')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t('deals.documents.filters.status')}</SelectItem>
						{ALL_STATUSES.map((s) => (
							<SelectItem key={s} value={s}>{t(DOCUMENT_STATUS_KEYS[s])}</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{sorted.length === 0 ? (
				<p className="text-sm text-(--color-muted-foreground)">{t('deals.documents.empty')}</p>
			) : (
				<div className="overflow-hidden rounded-(--radius-lg) border border-(--color-border) bg-(--color-background)">
					<Table className="table-fixed">
						<TableHeader>
							<TableRow className="h-12 hover:bg-transparent">
								<TableHead className="px-0 py-1">
									<Button
										variant="ghost"
										iconRight={<ArrowUpDownIcon className="size-4" />}
										className="h-auto min-w-20 gap-1 px-3 py-2 text-sm font-[500] leading-6 text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
										onClick={() => toggleSort('title')}
									>
										{t('deals.documents.columns.title')}
									</Button>
								</TableHead>
								<TableHead className="w-[164px] px-0 py-1 pr-1">
									<Button
										variant="ghost"
										iconRight={<ArrowUpDownIcon className="size-4" />}
										className="h-auto min-w-20 gap-1 px-3 py-2 text-sm font-[500] leading-6 text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
										onClick={() => toggleSort('createdAt')}
									>
										{t('deals.documents.columns.createdAt')}
									</Button>
								</TableHead>
								<TableHead className="w-[144px] px-4 py-3 text-sm font-[500] leading-6 text-(--color-muted-foreground)">
									{t('deals.documents.columns.status')}
								</TableHead>
								<TableHead className="w-[64px] px-4 py-3" />
							</TableRow>
						</TableHeader>
						<TableBody>
							{sorted.map((d) => (
								<TableRow
									key={d.id}
									className="h-[76px] cursor-pointer"
									onClick={() => void navigate({ to: '/documents/$documentId', params: { documentId: d.id }, search: { from: currentHref } })}
								>
									<TableCell className="h-[76px] px-4 py-0 text-sm leading-5 text-(--color-foreground)">
										{d.title}
									</TableCell>
									<TableCell className="h-[76px] w-[164px] px-4 py-0 text-sm leading-5 text-(--color-muted-foreground)">
										{formatCreatedAt(d.createdAt)}
									</TableCell>
									<TableCell className="h-[76px] w-[144px] px-4 py-0">
										<Badge variant={DOCUMENT_STATUS_VARIANTS[d.status]} size="md">
											{t(DOCUMENT_STATUS_KEYS[d.status])}
										</Badge>
									</TableCell>
									<TableCell className="h-[76px] w-[64px] px-4 py-0" onClick={(e) => e.stopPropagation()}>
										<DropdownMenu>
											<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('deals.documents.columns.actions')} />}>
												<EllipsisVerticalIcon className="size-4" />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="min-w-44">
												<DropdownMenuItem onClick={() => d.fileUrl && window.open(d.fileUrl, '_blank')}>
													{t('deals.documents.actions.download')}
												</DropdownMenuItem>
												{/* A generated document is named after its template
												    and an uploaded one after the file, so neither name
												    says which document it is. Until now that was the
												    one thing about it that could not be corrected. */}
												<DropdownMenuItem
													onClick={() => setRenaming({ id: d.id, title: d.title })}
												>
													{t('documents.rename.action')}
												</DropdownMenuItem>
												<DropdownMenuItem
													disabled={d.status !== 'DATA_FILLED'}
													onClick={() => void updateDocumentStatus({ id: d.id, body: { status: 'SIGNED_BY_ME' } })}
												>
													{t('deals.documents.actions.sign')}
												</DropdownMenuItem>
												<DropdownMenuItem disabled={d.status !== 'SIGNED_BY_ME'}
													onClick={() => void updateDocumentStatus({ id: d.id, body: { status: 'SENT' } })}>
													{t('deals.documents.actions.send')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className="text-(--color-destructive) focus:text-(--color-destructive)"
													onClick={() => void deleteDocument(d.id)}
												>
													{t('deals.documents.actions.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			<SelectTemplateDialog
				open={selectOpen}
				templates={templates}
				onOpenChange={setSelectOpen}
				onConfirm={handleTemplateConfirmed}
			/>

			<CreateDocumentWizard
				open={fillOpen}
				template={chosenTemplate}
				deal={wizardDeal}
				property={toWizardProperty(property, t)}
				existingNumbers={docs.map((d) => d.title.replace(/\D+/g, '')).filter(Boolean)}
				onOpenChange={(v) => {
					setFillOpen(v)
					if (!v) setChosenTemplate(null)
				}}
				onSave={handleDocumentSaved}
			/>
			{renaming && (
				<RenameDocumentDialog
					document={renaming}
					onOpenChange={() => setRenaming(null)}
				/>
			)}
		</div>
	)
}
